import { useTranslation } from 'react-i18next';
import LABELS from '../constants/rompslompLabels.json';

// DISPLAY-ONLY helpers for names that come from Rompslomp (ledger accounts, BTW codes, contacts). Nothing here is stored or sent: the stored value
// is the ledger account id, and the sender keeps using the ids and the Dutch names of the cache. Translations are keyed by the account CODE
// (path, e.g. profit.costs.purchased_stock), never by the Dutch name; an account without a translation is shown with its Dutch name unchanged.

const NAMED = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

// Rompslomp returns HTML-escaped names ("Telefoon &amp; internet"); decode them at display time only (the cache keeps the raw text).
export function decodeEntities(value) {
    if (value == null) return '';
    let text = String(value);
    for (let i = 0; i < 3 && text.includes('&'); i += 1) {      // &amp;amp; style double escaping
        const next = text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, body) => {
            if (body[0] === '#') {
                const code = body[1].toLowerCase() === 'x' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
                return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : match;
            }
            return Object.prototype.hasOwnProperty.call(NAMED, body.toLowerCase()) ? NAMED[body.toLowerCase()] : match;
        });
        if (next === text) break;
        text = next;
    }
    return text;
}

export const isEnglish = (language) => String(language || '').toLowerCase().startsWith('en');

export const accountDutchName = (account) => decodeEntities(account?.name);

export function accountEnglish(account) {
    const code = account?.path;
    const entry = code ? LABELS.accounts[code] : null;
    return entry ? entry.en : null;
}

// English UI: "Office supplies (Kantoorartikelen)"; Dutch UI (or no translation): the Dutch name only.
export function accountLabel(account, language) {
    const dutch = accountDutchName(account);
    if (!isEnglish(language)) return dutch;
    const en = accountEnglish(account);
    if (!en || en.toLowerCase() === dutch.toLowerCase()) return dutch;
    return `${en} (${dutch})`;
}

export function vatTypeEnglish(vatType) {
    const entry = vatType?.name ? LABELS.vat_types[vatType.name] : null;
    return entry ? entry.en : null;
}

// BTW code: English "High (hoog, vat_high, 0.21)"; Dutch "hoog (vat_high, 0.21)".
export function vatTypeLabel(vatType, language) {
    const dutch = decodeEntities(vatType?.friendly_name || vatType?.name);
    const detail = [vatType?.name, vatType?.value].filter((x) => x !== undefined && x !== null && x !== '').join(', ');
    const en = vatTypeEnglish(vatType);
    if (isEnglish(language) && en && en.toLowerCase() !== dutch.toLowerCase()) return `${en} (${dutch}${detail ? `, ${detail}` : ''})`;
    return `${dutch}${detail ? ` (${detail})` : ''}`;
}

// Every account / BTW code id inside a payload, with what will really be sent: the id and the Dutch name of the cache.
export function payloadSentAs(payload, accounts = [], vatTypes = [], language) {
    const accountIds = new Set();
    const vatIds = new Set();
    const walk = (node) => {
        if (Array.isArray(node)) { node.forEach(walk); return; }
        if (!node || typeof node !== 'object') return;
        Object.entries(node).forEach(([key, value]) => {
            if ((key === 'account_id' || key === 'type_account_id') && Number.isInteger(value)) accountIds.add(value);
            else if (key === 'vat_type_id' && Number.isInteger(value)) vatIds.add(value);
            else walk(value);
        });
    };
    walk(payload);
    const out = [];
    accountIds.forEach((id) => {
        const a = accounts.find((x) => x.id === id);
        out.push({ kind: 'account', id, label: a ? accountLabel(a, language) : String(id), dutch: a ? accountDutchName(a) : null });
    });
    vatIds.forEach((id) => {
        const v = vatTypes.find((x) => x.id === id);
        out.push({ kind: 'vat', id, label: v ? vatTypeLabel(v, language) : String(id), dutch: v ? decodeEntities(v.friendly_name || v.name) : null });
    });
    return out;
}

export function useRompslompLabels() {
    const { i18n } = useTranslation();
    const language = i18n?.language;
    return {
        language,
        account: (a) => accountLabel(a, language),
        vatType: (v) => vatTypeLabel(v, language),
        text: decodeEntities,
    };
}
